import { AccountsFactory, Avatar, Button, EStorageType, fetchRoute, formatNumber, GenericWrapperContext, getAdjustedBillingCycle, getBrandUrl, getCalendarFormat, getStorage, Icon, LinearProgress, LoadingContainer, setStorage, TAccountReport, Typography } from "@cakemail-org/ui-components-v2"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Stack } from "@mui/material"
import moment from "moment"
import { goToBilling, hasAccessToBilling } from "../../utils/billing"

export function MenuSettings({ onGearClick, onLogout }: { onGearClick: () => void, onLogout: () => void }) {
    const { partnerBrand, account, user } = useContext(GenericWrapperContext)
    const [openedSettings, setOpenedSettings] = useState<boolean>(false)
    const [accountStats, setAccountStats] = useState<TAccountReport>()
    const { t } = useTranslation()

    useEffect(() => {
        if (openedSettings) {
            fetchAccountStats()
        }
    }, [openedSettings])

    function fetchAccountStats() {
        const accountId = String(account.id)
        const accountStats = getStorage({ key: accountId + "-billingCycleStats", type: EStorageType.session })

        if (accountStats && accountStats.expires > moment().unix()) {
            setAccountStats(accountStats.report)
            return
        }

        if (["1", "131010", "133315"].includes(accountId)) {
            setAccountStats({} as TAccountReport)
            return
        }

        account.getReport({}).then((report) => {
            setAccountStats(report)
            setStorage({ key: accountId + "-billingCycleStats", data: { report: report, expires: moment().add(1, "h").unix() }, type: EStorageType.session })
        })
    }

    function handleToggleSettings() {
        setOpenedSettings(!openedSettings)
    }

    function getBillingProgress() {
        const { start, end } = getAdjustedBillingCycle(moment.unix(account.usage_limits.starts_on))
        const now = moment()

        const totalDuration = end.diff(start)
        const elapsed = now.diff(start)

        const progress = (elapsed / totalDuration) * 100
        return {
            start: start.unix(),
            end: end.unix(),
            progress: Math.min(Math.max(progress, 0), 100) // Clamp between 0-100%
        }
    }


    const locale = user?.language?.replace("_", "-") || "en-US"

    let contacts = 0,
        contactLimit = 0,
        contactUsage = 0,
        billingCycle,
        emails = 0,
        emailsMonthlyLimits = 0,
        emailUsage = 0,
        credits = null


    contactLimit = account.usage_limits.maximum_contacts
    emailsMonthlyLimits = account.usage_limits.per_month
    credits = account.usage_limits.remaining
    billingCycle = getBillingProgress()


    if (accountStats) {
        contacts = accountStats.active_contacts
        contactUsage = accountStats.contacts_usage
        emails = accountStats.sent_emails
        emailUsage = accountStats.emails_usage
    }

    return <Stack className="footer appMenuSettings" direction="row" gap={2} >
        <Avatar variant='square' email={user?.email} firstName={user?.first_name} lastName={user?.last_name} sizes={42} />

        <Stack direction="row" gap={2} justifyItems="space-between" className={`appMenuSettingsClosedButtons ${openedSettings ? "hidden" : "show"}`}>
            <Stack direction="row" gap={2} >
                <Button color="primary" size="small" variant='outlined' onClick={handleToggleSettings} >
                    <Avatar variant='square' email={user?.email} firstName={user?.first_name} lastName={user?.last_name} sizes={26} />
                    <img src={getBrandUrl({ brand: partnerBrand, elKey: "logo" })} alt="logo" />
                </Button>
                <Button iconOnly color="body1" size="medium" variant='outlined' onClick={onGearClick} >
                    <Icon name="Settings18" />
                </Button>
            </Stack>

            {hasAccessToBilling({ account, brand: partnerBrand }) &&
                <Button color="secondary" size="medium" variant='outlined' onClick={() => {
                    goToBilling({ account, user, brand: partnerBrand })
                }}>
                    {t("general-upgrade")}
                </Button>
            }
        </Stack>


        <Stack direction="column" gap={6} justifyContent="space-between" className={`appMenuSettingsOpened ${openedSettings ? "show" : "hidden"}`}>

            <Stack direction="row" gap={4} alignItems="center" className="userInfo">
                <Stack direction="row" gap={4} alignItems="center" >
                    <Avatar variant='square' email={user?.email} firstName={user?.first_name} lastName={user?.last_name} sizes={40} />
                    <Stack direction="column" >
                        <Typography variant="body1S" className="ellipsis" >{user?.first_name || ""} {user?.last_name || ""}</Typography>
                        <Typography variant="body1S" color="shade500.main" className="ellipsis" >{user?.email}</Typography>
                    </Stack>
                </Stack>

                <Icon name="X14" onClick={handleToggleSettings} />
            </Stack>



            <Stack gap={4} className="accountStats">

                <Stack gap={2} >
                    <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between" >
                        <Typography variant="body1S" className="bold">{t("general-subscriber_other")}</Typography>
                        <Typography variant="body1S" className="bold" >{formatNumber(contacts, locale) || "-"} / {formatNumber(contactLimit, locale)}</Typography>
                    </Stack>
                    <LinearProgress key={accountStats ? "determinate" : "indeterminate"} value={contactUsage || 0} color="secondary" size="small" variant={accountStats ? "determinate" : "indeterminate"} />
                </Stack>


                <Stack gap={2} >
                    <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between" >
                        <Typography variant="body1S" className="bold">{t("general-email-sent")}</Typography>
                        <Typography variant="body1S" className="bold" >
                            {credits === null &&
                                <>
                                    {formatNumber(emails, locale) || "-"} / {formatNumber(emailsMonthlyLimits, locale)}
                                </>
                            }
                            {credits !== null &&
                                <>
                                    {formatNumber(emails, locale)}
                                </>
                            }
                        </Typography>
                    </Stack>
                    {credits === null &&
                        <LinearProgress key={accountStats ? "determinate" : "indeterminate"} value={emailUsage || 0} color="secondary" size="small" variant={accountStats ? "determinate" : "indeterminate"} />
                    }
                </Stack>

                {credits !== null &&
                    <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between" >
                        <Typography variant="body1S" className="bold">{t("general-credit-remaining")}</Typography>
                        <Typography variant="body1S" className="bold" >{formatNumber(credits || 0, locale)} </Typography>
                    </Stack>
                }

                <Stack gap={2} >
                    <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between" >
                        <Typography variant="body1S" className="bold" >{t("general-billing-cycle")}</Typography>
                        <Typography variant="body1S" className="bold" >{getCalendarFormat({ unixDate: billingCycle.start, showTime: false })} - {getCalendarFormat({ unixDate: billingCycle.end, showTime: false })}</Typography>
                    </Stack>
                    <LinearProgress value={billingCycle.progress} color="secondary" size="small" variant={"determinate"} />
                </Stack>

            </Stack>


            <Stack direction="row" gap={4} justifyContent="flex-end" >
                <Button color="shade100" size="small" variant='contained' onClick={onLogout} >
                    {t("general-logout")}
                </Button>
            </Stack>


        </Stack>

    </Stack>
}